import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import {
  TrendingUp,
  PieChart,
  LineChart,
  Shield,
  Zap,
  Users,
  ChevronRight,
  Check
} from 'lucide-react';
import { useAuth } from '../context/AuthContext';

const LandingPage = () => {
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();

  React.useEffect(() => {
    if (isAuthenticated) {
      navigate('/dashboard');
    }
  }, [isAuthenticated, navigate]);

  const features = [
    {
      icon: <LineChart className="w-6 h-6" />,
      title: "Real-Time Charts",
      description: "Interactive price charts with multiple timeframes and technical indicators"
    },
    {
      icon: <TrendingUp className="w-6 h-6" />,
      title: "Paper Trading",
      description: "Practice buying and selling stocks with $10,000 in virtual money"
    },
    {
      icon: <PieChart className="w-6 h-6" />,
      title: "Portfolio Analytics",
      description: "Track your performance with detailed P&L analysis and equity curves"
    },
    {
      icon: <Shield className="w-6 h-6" />,
      title: "Secure 2FA",
      description: "Protect your account with mandatory two-factor authentication"
    },
    {
      icon: <Zap className="w-6 h-6" />,
      title: "Lightning Fast",
      description: "Real-time stock quotes powered by Finnhub market data API"
    },
    {
      icon: <Users className="w-6 h-6" />,
      title: "Learn Risk-Free",
      description: "Perfect for beginners learning to invest without real money risk"
    }
  ];

  const tiers = [
    {
      name: "Free",
      price: "$0",
      period: "/forever",
      description: "Perfect for getting started",
      features: [
        "10 stocks in watchlist",
        "20 trades per month",
        "1 year price history",
        "Basic portfolio analytics"
      ],
      cta: "Get Started Free",
      highlighted: false
    },
    {
      name: "Pro",
      price: "$9.99",
      period: "/month",
      description: "For serious paper traders",
      features: [
        "100 stocks in watchlist",
        "Unlimited trades",
        "Full price history",
        "Advanced analytics",
        "Priority support"
      ],
      cta: "Upgrade to Pro",
      highlighted: true
    }
  ];

  return (
    <div className="min-h-screen bg-[#F9F9F7]">
      {/* Header */}
      <header className="fixed top-0 left-0 right-0 z-50 glass border-b border-stone-200/50">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="flex items-center justify-between h-16">
            <Link to="/" className="flex items-center gap-2">
              <div className="w-8 h-8 bg-[#1A4D2E] rounded-lg flex items-center justify-center">
                <TrendingUp className="w-5 h-5 text-white" />
              </div>
              <span className="font-serif text-xl font-semibold text-[#1A4D2E]">FictoInvest</span>
            </Link>
            <nav className="hidden md:flex items-center gap-8">
              <Link to="/features" className="text-sm font-medium text-gray-600 hover:text-[#1A4D2E] transition-colors">Features</Link>
              <Link to="/pricing" className="text-sm font-medium text-gray-600 hover:text-[#1A4D2E] transition-colors">Pricing</Link>
              <Link to="/about" className="text-sm font-medium text-gray-600 hover:text-[#1A4D2E] transition-colors">About</Link>
              <Link to="/contact" className="text-sm font-medium text-gray-600 hover:text-[#1A4D2E] transition-colors">Contact</Link>
            </nav>
            <div className="flex items-center gap-3">
              <Button
                variant="ghost"
                onClick={() => navigate('/login')}
                className="text-[#1A4D2E] hover:bg-green-50"
                data-testid="login-btn"
              >
                Log In
              </Button>
              <Button
                onClick={() => navigate('/register')}
                className="bg-[#1A4D2E] hover:bg-[#143d24] text-white shadow-lg shadow-green-900/20"
                data-testid="signup-btn"
              >
                Sign Up Free
              </Button>
            </div>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="pt-32 pb-20 px-6 md:px-12">
        <div className="max-w-7xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="space-y-8 animate-slide-up">
              <div className="inline-flex items-center gap-2 px-4 py-2 bg-green-50 rounded-full border border-green-100">
                <span className="w-2 h-2 bg-[#C1F23D] rounded-full animate-pulse"></span>
                <span className="text-sm font-medium text-[#1A4D2E]">Paper Trading Simulator</span>
              </div>

              <h1 className="font-serif text-5xl md:text-7xl leading-[0.95] text-[#1F2937] tracking-tight">
                Learn to invest
                <span className="block text-[#1A4D2E]">without the risk</span>
              </h1>

              <p className="text-lg md:text-xl text-gray-600 leading-relaxed max-w-lg">
                Practice stock trading with virtual money. Track real market prices, build your portfolio,
                and master investing—all without risking a single dollar.
              </p>

              <div className="flex flex-col sm:flex-row gap-4">
                <Button
                  size="lg"
                  onClick={() => navigate('/register')}
                  className="bg-[#C1F23D] text-[#1A4D2E] hover:bg-[#b2e030] shadow-lg shadow-lime-400/20 font-bold px-8 py-6 text-lg"
                  data-testid="hero-cta-btn"
                >
                  Start with $10,000 <ChevronRight className="ml-2 w-5 h-5" />
                </Button>
                <Button
                  size="lg"
                  variant="outline"
                  onClick={() => navigate('/login')}
                  className="border-[#1A4D2E]/20 text-[#1A4D2E] hover:bg-green-50 px-8 py-6 text-lg"
                >
                  I have an account
                </Button>
              </div>

              <div className="flex items-center gap-6 pt-4">
                <div className="flex -space-x-2">
                  {[1, 2, 3, 4].map(i => (
                    <div
                      key={i}
                      className="w-10 h-10 rounded-full bg-gradient-to-br from-green-100 to-green-200 border-2 border-white flex items-center justify-center text-xs font-medium text-[#1A4D2E]"
                    >
                      {String.fromCharCode(64 + i)}
                    </div>
                  ))}
                </div>
                <p className="text-sm text-gray-600">
                  <span className="font-semibold text-[#1A4D2E]">2,000+</span> learners already practicing
                </p>
              </div>
            </div>

            <div className="relative animate-fade-in stagger-2">
              <div className="absolute inset-0 bg-gradient-to-br from-green-100/50 to-transparent rounded-3xl transform rotate-3"></div>
              <img
                src="https://images.unsplash.com/photo-1611974789855-9c2a0a7236a3?crop=entropy&cs=srgb&fm=jpg&q=85&w=800"
                alt="Stock market trading charts and analytics"
                className="relative rounded-3xl shadow-2xl shadow-green-900/10 w-full"
              />
              <Card className="absolute -bottom-6 -left-6 p-4 bg-white/90 backdrop-blur border-green-100 shadow-float">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 bg-green-100 rounded-xl flex items-center justify-center">
                    <TrendingUp className="w-6 h-6 text-[#1A4D2E]" />
                  </div>
                  <div>
                    <p className="text-xs text-gray-500 uppercase tracking-wider">Portfolio Value</p>
                    <p className="text-xl font-bold text-[#1A4D2E] tabular-nums">$12,847.32</p>
                  </div>
                </div>
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section id="features" className="py-24 px-6 md:px-12 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="font-serif text-3xl md:text-5xl text-[#1F2937] mb-4">
              Everything you need to practice investing
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Real market data, virtual money, and powerful tools to help you learn the art of investing.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {features.map((feature, index) => (
              <Card
                key={index}
                className="group bg-white border-stone-100 hover:border-green-500/20 hover:shadow-card-hover transition-all duration-300 card-hover"
              >
                <CardContent className="p-6">
                  <div className="w-12 h-12 bg-green-50 rounded-xl flex items-center justify-center mb-4 group-hover:bg-[#1A4D2E] group-hover:text-white transition-colors">
                    {feature.icon}
                  </div>
                  <h3 className="font-serif text-xl font-semibold text-[#1F2937] mb-2">{feature.title}</h3>
                  <p className="text-gray-600">{feature.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Image Section */}
      <section className="py-24 px-6 md:px-12">
        <div className="max-w-7xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="order-2 lg:order-1">
              <img
                src="https://images.unsplash.com/photo-1759296682393-a5c3695080fa?crop=entropy&cs=srgb&fm=jpg&q=85&w=600"
                alt="Modern minimalist office"
                className="rounded-2xl shadow-xl"
              />
            </div>
            <div className="order-1 lg:order-2 space-y-6">
              <h2 className="font-serif text-3xl md:text-4xl text-[#1F2937]">
                Track anywhere, anytime
              </h2>
              <p className="text-lg text-gray-600 leading-relaxed">
                Whether you're at your desk or on the go, FictoInvest keeps you connected to the markets.
                Monitor your portfolio, execute trades, and analyze performance from any device.
              </p>
              <ul className="space-y-3">
                {["Real-time price updates", "Mobile-responsive design", "Instant trade execution"].map((item, i) => (
                  <li key={i} className="flex items-center gap-3">
                    <div className="w-5 h-5 bg-[#C1F23D] rounded-full flex items-center justify-center">
                      <Check className="w-3 h-3 text-[#1A4D2E]" />
                    </div>
                    <span className="text-gray-700">{item}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      <section id="pricing" className="py-24 px-6 md:px-12 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="font-serif text-3xl md:text-5xl text-[#1F2937] mb-4">
              Simple, transparent pricing
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Start free and upgrade when you need more. No hidden fees, no surprises.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-8 max-w-4xl mx-auto">
            {tiers.map((tier, index) => (
              <Card
                key={index}
                className={`relative overflow-hidden ${tier.highlighted
                  ? 'border-[#1A4D2E] border-2 shadow-xl'
                  : 'border-stone-200'
                  }`}
              >
                {tier.highlighted && (
                  <div className="absolute top-0 right-0 bg-[#C1F23D] text-[#1A4D2E] text-xs font-bold px-3 py-1 rounded-bl-lg">
                    POPULAR
                  </div>
                )}
                <CardContent className="p-8">
                  <h3 className="font-serif text-2xl font-semibold text-[#1F2937] mb-2">{tier.name}</h3>
                  <p className="text-gray-600 mb-6">{tier.description}</p>
                  <div className="mb-6">
                    <span className="text-4xl font-bold text-[#1A4D2E]">{tier.price}</span>
                    <span className="text-gray-500">{tier.period}</span>
                  </div>
                  <ul className="space-y-3 mb-8">
                    {tier.features.map((feature, i) => (
                      <li key={i} className="flex items-center gap-3">
                        <Check className="w-5 h-5 text-[#10B981]" />
                        <span className="text-gray-700">{feature}</span>
                      </li>
                    ))}
                  </ul>
                  <Button
                    className={`w-full ${tier.highlighted
                      ? 'bg-[#1A4D2E] hover:bg-[#143d24] text-white'
                      : 'bg-white border-2 border-[#1A4D2E] text-[#1A4D2E] hover:bg-green-50'
                      }`}
                    onClick={() => navigate('/register')}
                    data-testid={`pricing-${tier.name.toLowerCase()}-btn`}
                  >
                    {tier.cta}
                  </Button>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 px-6 md:px-12">
        <div className="max-w-4xl mx-auto text-center">
          <img
            src="https://images.unsplash.com/photo-1573165381639-1100ef3c5e3c?crop=entropy&cs=srgb&fm=jpg&q=85&w=400"
            alt="Students learning"
            className="w-32 h-32 rounded-full object-cover mx-auto mb-8 shadow-xl"
          />
          <h2 className="font-serif text-3xl md:text-4xl text-[#1F2937] mb-4">
            Ready to start your investing journey?
          </h2>
          <p className="text-lg text-gray-600 mb-8">
            Join thousands of learners who are mastering the stock market risk-free.
          </p>
          <Button
            size="lg"
            onClick={() => navigate('/register')}
            className="bg-[#C1F23D] text-[#1A4D2E] hover:bg-[#b2e030] shadow-lg shadow-lime-400/20 font-bold px-12 py-6 text-lg"
          >
            Create Free Account <ChevronRight className="ml-2 w-5 h-5" />
          </Button>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-12 px-6 md:px-12 border-t border-stone-200 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="flex flex-col md:flex-row justify-between items-center gap-6">
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 bg-[#1A4D2E] rounded-lg flex items-center justify-center">
                <TrendingUp className="w-5 h-5 text-white" />
              </div>
              <span className="font-serif text-xl font-semibold text-[#1A4D2E]">FictoInvest</span>
            </div>
            <div className="text-center">
              <p className="text-sm text-gray-500 mb-2">
                Educational simulator only. Not financial advice. No real trading.
              </p>
              <div className="flex items-center justify-center gap-4">
                <Link to="/terms" className="text-sm text-gray-500 hover:text-[#1A4D2E] transition-colors">
                  Terms & Conditions
                </Link>
                <span className="text-gray-300">|</span>
                <Link to="/privacy" className="text-sm text-gray-500 hover:text-[#1A4D2E] transition-colors">
                  Privacy Policy
                </Link>
              </div>
            </div>
            <p className="text-sm text-gray-500">
              © {new Date().getFullYear()} FictoInvest. All rights reserved.
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default LandingPage;
